import type { AgendaProject, AgendaTask } from '../../api';

/**
 * Sorting the schedule into a to-do list - pure, tested. "Due" is measured
 * against the planned finish and, where set, the latest acceptable date.
 */
export type Bucket = 'overdue' | 'today' | 'week' | 'soon' | 'later' | 'done';

export const BUCKETS: readonly { key: Bucket; label: string }[] = [
  { key: 'overdue', label: 'Überfällig' },
  { key: 'today', label: 'Heute' },
  { key: 'week', label: 'Diese Woche' },
  { key: 'soon', label: 'Nächste 14 Tage' },
  { key: 'later', label: 'Später' },
  { key: 'done', label: 'Erledigt' },
];

export interface TodoItem extends AgendaTask {
  projectId: string;
  projectNumber: string;
  projectName: string;
  /** The date that decides the bucket: the earlier of finish and "spätestens". */
  due: string | null;
}

const addDays = (iso: string, n: number): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Sunday of the week of `today` (weeks run Monday to Sunday). */
export function endOfWeek(today: string): string {
  const day = new Date(`${today}T00:00:00Z`).getUTCDay();
  return addDays(today, (7 - day) % 7);
}

export function bucketOf(item: TodoItem, today: string): Bucket {
  if (item.status === 'done') return 'done';
  const due = item.due;
  if (!due) return 'later';
  if (due < today) return 'overdue';
  // Running today: started, not yet finished.
  if (due === today || (item.start !== null && item.start <= today)) return 'today';
  if (due <= endOfWeek(today) || (item.start !== null && item.start <= endOfWeek(today))) return 'week';
  if ((item.start ?? due) <= addDays(today, 14)) return 'soon';
  return 'later';
}

export function flatten(projects: readonly AgendaProject[]): TodoItem[] {
  return projects.flatMap((p) =>
    p.tasks.map((t) => {
      const candidates = [t.finish, t.deadline].filter((d): d is string => !!d).sort();
      return { ...t, projectId: p.projectId, projectNumber: p.number, projectName: p.name, due: candidates[0] ?? null };
    }),
  );
}

/**
 * A colour per project, so the calendar reads at a glance: muted tones that
 * sit with the plum of the interface, handed out in the order of the
 * projects (by number), so the first project wears the house colour. No
 * second purple beside it: grape and violet would read as the same.
 */
const PALETTE = ['brand', 'teal', 'orange', 'indigo', 'lime', 'cyan', 'yellow', 'gray'];
export function projectColors(projectIds: readonly string[]): Map<string, string> {
  return new Map(projectIds.map((id, i) => [id, PALETTE[i % PALETTE.length]!]));
}

/** Whether a task runs on a day - start and finish included. */
export const runsOn = (t: { start: string | null; finish: string | null }, day: string): boolean =>
  t.start !== null && t.finish !== null && t.start <= day && day <= t.finish;

/** A task as a bar across one week row of the calendar. */
export interface WeekBar {
  item: TodoItem;
  /** First day column, 0 = Monday. */
  col: number;
  /** Number of days covered in this week. */
  span: number;
  /** Row inside the week, 0 = top. */
  lane: number;
  /** The task began before this week / goes on after it. */
  fromBefore: boolean;
  toAfter: boolean;
}

const dayIndex = (from: string, day: string): number => Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

/**
 * Lays out the tasks of one week as bars, as a desk calendar does: each task
 * one bar over the days it runs, cut at the week's edges, on the first lane
 * where it fits. Bars are placed from the left; of those starting on the same
 * day the longer one first, so it stays on top and short ones fill the gaps.
 */
export function layoutWeek(items: readonly TodoItem[], weekStart: string): { bars: WeekBar[]; lanes: number } {
  const weekEnd = addDays(weekStart, 6);
  const placed = items
    .filter((t) => t.start !== null)
    .map((t) => ({ item: t, start: t.start!, end: t.milestone || !t.finish ? t.start! : t.finish }))
    .filter((t) => t.start <= weekEnd && t.end >= weekStart)
    .map((t) => {
      const col = Math.max(0, dayIndex(weekStart, t.start));
      const last = Math.min(6, dayIndex(weekStart, t.end));
      return { ...t, col, span: last - col + 1, fromBefore: t.start < weekStart, toAfter: t.end > weekEnd };
    })
    .sort((a, b) => a.col - b.col || b.span - a.span || a.item.projectNumber.localeCompare(b.item.projectNumber) || a.item.code.localeCompare(b.item.code, 'de', { numeric: true }));
  const laneEnds: number[] = [];
  const bars: WeekBar[] = placed.map((p) => {
    let lane = laneEnds.findIndex((end) => end < p.col);
    if (lane < 0) { lane = laneEnds.length; laneEnds.push(-1); }
    laneEnds[lane] = p.col + p.span - 1;
    return { item: p.item, col: p.col, span: p.span, lane, fromBefore: p.fromBefore, toAfter: p.toAfter };
  });
  return { bars, lanes: laneEnds.length };
}
